import { Component, Input, OnInit } from '@angular/core';
import { InvoiceStatusComponent } from "../invoice-status/invoice-status.component";
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { CommonModule } from '@angular/common';
import { InvoiceGasCostAnalysisChartComponent } from "../../charts/gas/invoice-gas-cost-analysis-chart/invoice-gas-cost-analysis-chart.component";
import { InvoiceGasDoughnutChartComponent } from "../../charts/gas/invoice-gas-doughnut-chart/invoice-gas-doughnut-chart.component";
import { TranslocoPipe } from '@jsverse/transloco';
import { GasInvoice, InvoiceGasPDRGet, InvoiceStatus, VisibilityStatus } from '../../../../core/interfaces/invoice-interfaces';
import { InvoiceHelperService } from '../../../../core/services/invoice-helper.service';
import { IsoToFormattedPeriodPipe } from '../../../../core/pipes/iso-to-formatted-period.pipe';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { InvoiceFileDisputeDialogComponent } from '../invoice-file-dispute-dialog/invoice-file-dispute-dialog.component';
import { InvoiceStatusTextComponent } from "../invoice-status-text/invoice-status-text.component";
import { FileUploadEnergyType } from '../../../../core/interfaces/file-upload-interfaces';

@Component({
  selector: 'app-expanded-gas-invoice',
  standalone: true,
  imports: [InvoiceStatusComponent, CustomButtonComponent, CommonModule, InvoiceGasCostAnalysisChartComponent, InvoiceGasDoughnutChartComponent, TranslocoPipe, IsoToFormattedPeriodPipe, InvoiceStatusTextComponent],
  templateUrl: './expanded-gas-invoice.component.html',
  styleUrl: './expanded-gas-invoice.component.css'
})
export class ExpandedGasInvoiceComponent implements OnInit {
  @Input() invoice: GasInvoice | null = null;
  @Input() statusOkColor!: string;
  @Input() disputeDialog!: InvoiceFileDisputeDialogComponent;
  @Input() reloadCallbackFunction!: () => void;

  summedPDR?: InvoiceGasPDRGet;

  // Gas doughnut chart
  chartData: number[] = [0.0, 0.0, 0.0, 0.0];
  smcNumber: number = 0;

  // Gas spread index chart
  chartSpreadIndex: number[] = [0.0, 0.0];

  // Toast
  @Input() toast!: ToastComponent;

  InvoiceStatus = InvoiceStatus;
  VisibilityStatus = VisibilityStatus;

  // Missing data
  forPeriodInput: string = '';
  forPeriodInputError: string | null = null;
  forPeriodDate: Date | undefined;

  invoiceNumberInput: string = '';
  invoiceNumberInputError: string | null = null;

  totalConsumptionInput: string = '';
  totalConsumptionInputError: string | null = null;

  totalCostsInput: string = '';
  totalCostsInputError: string | null = null;

  totalTaxesInput: string = '';
  totalTaxesInputError: string | null = null;

  reactiveEnergyCostsInput: string = '';
  reactiveEnergyCostsInputError: string | null = null;

  pdrIdInput: string = '';
  pdrIdInputError: string | null = null;

  variableGasConsumptionInput: string = '';
  variableGasConsumptionInputError: string | null = null;

  spreadInput: string = '';
  spreadInputError: string | null = null;

  fixedGasConsumptionInput: string = '';
  fixedGasConsumptionInputError: string | null = null;

  priceTypeInput: string = '';
  priceTypeInputError: string | null = null;

  constructor(private invoiceHelper: InvoiceHelperService, private invoiceService: InvoiceService, private apiError: ApiErrorService) { }

  ngOnInit(): void {
    if (this.invoice == null) {
      return;
    }

    if (this.invoice) {
      this.summedPDR = this.invoiceHelper.getGasInvoicePDRSummed(this.invoice);
    }
    this.populateGasDoughnutChart(this.invoice!, this.summedPDR!);
    this.populateGasSpreadIndexChart(this.invoice!, this.summedPDR!);
  }

  getInvoiceStatus(invoice: GasInvoice): boolean {
    if (invoice.status == InvoiceStatus.CALCULATION_FAILED) {
      return false;
    }
    return this.invoiceHelper.getGasInvoiceStatus(invoice);
  }

  populateGasDoughnutChart(invoice: GasInvoice, summedPDR: InvoiceGasPDRGet) {
    if (!invoice) {
      this.chartData = [0.0, 0.0, 0.0, 0.0];
      this.smcNumber = 0;
      return;
    }

    const totalConsumption = summedPDR.month_gas_consumption;
    const netInvoiceAmount = summedPDR.calc_data?.invoice_amount_net_calc ?? invoice.invoice_amount_net_actual ?? 0;

    if (netInvoiceAmount === 0 || totalConsumption === 0) {
      this.chartData = [0.0, 0.0, 0.0, 1.0]; // Show unknown only
      this.smcNumber = 0;
      return;
    }

    // Energy cost
    const varCost = summedPDR.calc_data?.material_costs_gas_variable_calc ?? 0;
    const fixedCost = summedPDR.calc_data?.material_costs_gas_fixed_calc ?? 0;
    const energyPerUnit = (varCost !== 0 ? varCost : fixedCost) / totalConsumption;

    // TMS Charges
    const tmsTotal =
      (summedPDR.fixed_distribution_costs_gas_calc ?? 0) +
      (summedPDR.calc_data?.var_transportation_costs_calc ?? 0) +
      (summedPDR.calc_data?.total_var_distribution_costs_calc ?? 0);
    const tmsPerUnit = tmsTotal / totalConsumption;

    // Taxes
    const taxesPerUnit = (summedPDR.calc_data?.total_gas_taxes_calc ?? 0) / totalConsumption;

    // Actual unit cost
    const actualUnitCost = netInvoiceAmount / totalConsumption;

    // Calculate unknown
    const knownSum = energyPerUnit + tmsPerUnit + taxesPerUnit;
    const unknown = actualUnitCost > knownSum ? actualUnitCost - knownSum : 0;

    // Set chart data in correct order
    this.chartData = [
      parseFloat(energyPerUnit.toFixed(2)),
      parseFloat(tmsPerUnit.toFixed(2)),
      parseFloat(taxesPerUnit.toFixed(2)),
      parseFloat(unknown.toFixed(2))
    ];


    // Set unit cost per smc
    this.smcNumber = parseFloat(actualUnitCost.toFixed(2));
  }

  populateGasSpreadIndexChart(invoice: GasInvoice, summedPDR: InvoiceGasPDRGet) {
    if (!invoice || !summedPDR) {
      this.chartSpreadIndex = [0.0, 0.0];
      return;
    }

    this.chartSpreadIndex = [
      parseFloat((summedPDR.spread ?? 0).toFixed(2)),
      parseFloat((summedPDR.forecast_index_value ?? 0).toFixed(2))
    ];
  }

  async downloadDisputeReport() {

    // Check if invoice is available
    if (this.invoice == null) {
      return
    }

    if (this.invoice.status == InvoiceStatus.CALCULATION_FAILED) {
      this.toast.showToast("shared.error", "invoice.not_enough_data", 'error', 5000, true);
      return;
    }

    // Download report
    try {
      var responseBlob = await lastValueFrom(this.invoiceService.getGasDispute(this.invoice.id ?? ''));

      const blobUrl = URL.createObjectURL(responseBlob);

      window.open(blobUrl, '_blank');
    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
  }

  async disputeReport() {
    if (!this.invoice || this.getInvoiceStatus(this.invoice)) { return; }
    await this.disputeDialog.openModal(this.invoice.id, this.toast, this.invoice.review_requested, FileUploadEnergyType.GAS);
  }

}
